import React, { useEffect, useMemo, useState } from 'react'
import { Gem, Loader, XCircle, Package, Edit3 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import tjsApi from '../services/tjsApi'
import kgsApi from '../services/kgsApi'
import uahApi from '../services/uahApi'
import kzt2Api from '../services/kzt2Api'
import bynApi from '../services/bynApi'
import { userApi } from '../../../services/userApi'
import useAppStore from '../../../store/useAppStore'
import { closePreparedPaymentWindow, prepareExternalPaymentWindow } from '../utils/openExternalPaymentLink'

/**
 * TjsPayment
 * Оплата в таджикских сомони (TJS) через Noros
 * Поддерживает два режима: готовые пакеты и произвольная сумма (TJS/кристаллы)
 * По доке: пакеты отправляем по crystal, свободный ввод — по tjs
 */
const TjsPayment = ({ method = 'tjs1' }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isKgs = method === 'kgs1'
  const isUah = method === 'uah1'
  const isKzt2 = method === 'kzt2'
  const isByn = method === 'byn1'
  const currency = isByn ? 'BYN' : isUah ? 'UAH' : isKzt2 ? 'KZT' : isKgs ? 'KGS' : 'TJS'
  const currencyKey = currency.toLowerCase()
  const statusKey = isKzt2 ? 'uah' : currencyKey
  const api = isByn ? bynApi : isUah ? uahApi : isKzt2 ? kzt2Api : isKgs ? kgsApi : tjsApi
  const roundAmount = (amount) => isByn ? Math.round(amount * 10) / 10 : Math.round(amount)
  const formatAmount = (amount) => isByn ? Number(amount).toFixed(2) : isKgs || isUah || isKzt2 ? formatCrystals(amount) : amount
  const minKey = `min_${currencyKey}`
  const maxKey = `max_${currencyKey}`
  const priceKey = `price_one_${currencyKey}_in_crystal`
  const crystalPriceKey = `price_one_crystal_in_${currencyKey}`
  const [pendingPayment, setPendingPayment] = useState(null)
  const [paymentConfirmed, setPaymentConfirmed] = useState(false)

  // Состояния для табов
  const [activeTab, setActiveTab] = useState('packages') // packages | custom

  // Конфигурация tjs1 (курсы и лимиты)
  const [tjsConfig, setTjsConfig] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [configError, setConfigError] = useState(null)

  // Пакеты
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [packagesError, setPackagesError] = useState(null)

  // Произвольная сумма
  const [tjsInput, setTjsInput] = useState('')
  const [crystalsInput, setCrystalsInput] = useState('')
  const [customAmountError, setCustomAmountError] = useState('')

  // Обработка платежа
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingPackageId, setProcessingPackageId] = useState(null)

  useEffect(() => {
    loadTjsConfig()
  }, [])

  useEffect(() => {
    loadPackages()
  }, [])

  useEffect(() => {
    if (!pendingPayment || paymentConfirmed) return undefined
    let active = true
    let checking = false
    const checkBalance = async () => {
      if (checking || document.visibilityState === 'hidden') return
      checking = true
      try {
        const response = await userApi.getProfile()
        const balance = Number(response?.me?.balance ?? response?.me?.points)
        if (active && Number.isFinite(balance) && balance > pendingPayment.balance) {
          setPaymentConfirmed(true)
        }
      } catch (error) {
        console.error('Error checking payment balance:', error)
      } finally {
        checking = false
      }
    }
    const interval = setInterval(checkBalance, 15000)
    const onVisible = () => { if (document.visibilityState === 'visible') checkBalance() }
    document.addEventListener('visibilitychange', onVisible)
    checkBalance()
    return () => {
      active = false
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pendingPayment, paymentConfirmed])

  const rates = useMemo(() => {
    const priceOneCrystalInTjs = parseFloat(tjsConfig?.[crystalPriceKey] || '0')
    const priceOneTjsInCrystal = parseFloat(tjsConfig?.[priceKey] || '0')

    return {
      priceOneCrystalInTjs,
      priceOneTjsInCrystal
    }
  }, [tjsConfig, crystalPriceKey, priceKey])

  // Форматирование кристаллов с пробелами для отображения
  const formatCrystals = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  const loadTjsConfig = async () => {
    setIsLoadingConfig(true)
    setConfigError(null)

    try {
      const config = await (isByn ? api.fetchBynConfig() : isUah ? api.fetchUahConfig() : isKzt2 ? api.fetchKzt2Config() : isKgs ? api.fetchKgsConfig() : api.fetchTjsConfig())
      setTjsConfig(config)
      console.log('✅ Payment config loaded:', config)
    } catch (error) {
      console.error('❌ Error loading payment config:', error)
      setConfigError(t('payment.sbp.configError'))
    } finally {
      setIsLoadingConfig(false)
    }
  }

  const loadPackages = async () => {
    setIsLoadingPackages(true)
    setPackagesError(null)

    try {
      const tjsPackages = await (isByn ? api.fetchBynPackages() : isUah ? api.fetchUahPackages() : isKzt2 ? api.fetchKzt2Packages() : isKgs ? api.fetchKgsPackages() : api.fetchTjsPackages())

      // Сортируем пакеты: самый дешевый первый, затем популярные
      const sortedPackages = [...tjsPackages].sort((a, b) => {
        const minPrice = Math.min(...tjsPackages.map((p) => p[method]))

        if (a[method] === minPrice && b[method] !== minPrice) return -1
        if (b[method] === minPrice && a[method] !== minPrice) return 1

        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1

        return a[method] - b[method]
      })

      setPackages(sortedPackages)
      console.log('✅ Payment packages loaded:', sortedPackages)
    } catch (error) {
      console.error('❌ Error loading payment packages:', error)
      setPackagesError(t('payment.sbp.packagesError'))
    } finally {
      setIsLoadingPackages(false)
    }
  }

  const openPaymentLink = (paymentLink, paymentWindow = null, startingBalance = 0) => {
    const onRedirect = () => {
      if (isKgs || isUah || isByn || isKzt2) {
        setPendingPayment({ link: paymentLink, balance: startingBalance })
        return
      }
      toast.success(t('payment.sbp.paymentNotification'), {
        duration: 4000,
        icon: '💳'
      })
      setTimeout(() => navigate('/'), 1500)
    }
    if (isByn) api.openBynPayment(paymentLink, onRedirect, paymentWindow)
    else if (isUah) api.openUahPayment(paymentLink, onRedirect, paymentWindow)
    else if (isKzt2) api.openKzt2Payment(paymentLink, onRedirect, paymentWindow)
    else if (isKgs) api.openKgsPayment(paymentLink, onRedirect, paymentWindow)
    else api.openTjsPayment(paymentLink, onRedirect, paymentWindow)
  }

  const handlePackagePayment = async (pkg) => {
    if (isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()
    const startingBalance = useAppStore.getState().balance

    setIsProcessing(true)
    setProcessingPackageId(pkg.id)

    try {
      console.log('💰 Создание платежа для пакета:', pkg)

      // Для пакета отправляем сумму в кристаллах (по доке TJS)
      const { link } = await api.createPaymentByCrystals(pkg.crystals)
      openPaymentLink(link, paymentWindow, startingBalance)
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа:', error)

      if (error.message === 'INVALID_AMOUNT') {
        toast.error((isKgs || isUah || isByn || isKzt2) && !tjsConfig ? t('payment.createError') : t(`payment.${currencyKey}.errorInvalidAmount`, {
          min: formatAmount(tjsConfig?.[minKey] ?? 10),
          max: formatAmount(tjsConfig?.[maxKey] ?? 18000)
        }))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else if (error.message === 'LIMIT_ORDER') {
        toast.error(t(`payment.${isKzt2 ? 'kzt2' : currencyKey}.errorLimitOrder`))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
      setProcessingPackageId(null)
    }
  }

  const handleTjsChange = (e) => {
    const value = isByn
      ? e.target.value.replace(',', '.').replace(/[^\d.]/g, '')
      : e.target.value.replace(/[^\d]/g, '')
    if (isByn && !/^\d*(?:\.\d?)?$/.test(value)) return
    setTjsInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneTjsInCrystal) {
      const tjs = parseFloat(value)
      if (Number.isFinite(tjs)) {
        // По доке: кристаллы = floor(tjs × price_one_tjs_in_crystal)
        const crystals = Math.floor(roundAmount(tjs) * rates.priceOneTjsInCrystal)
        setCrystalsInput(crystals.toString())
      } else {
        setCrystalsInput('')
      }
    } else {
      setCrystalsInput('')
    }
  }

  const handleCrystalsChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '')
    setCrystalsInput(value)
    setCustomAmountError('')

    if (value && rates.priceOneCrystalInTjs) {
      const crystals = parseFloat(value)
      if (Number.isFinite(crystals)) {
        // По доке: TJS = round(crystals × price_one_crystal_in_tjs)
        const tjs = roundAmount(crystals * rates.priceOneCrystalInTjs)
        setTjsInput(tjs.toString())
      } else {
        setTjsInput('')
      }
    } else {
      setTjsInput('')
    }
  }

  const validateCustomAmount = () => {
    if (!tjsInput || !tjsConfig) {
      setCustomAmountError(t('payment.sbp.enterAmount'))
      return false
    }

    const tjs = parseFloat(tjsInput)
    if (!Number.isFinite(tjs)) {
      setCustomAmountError(t('payment.crypto.enterCorrectAmount'))
      return false
    }

    if (roundAmount(tjs) < tjsConfig[minKey] || roundAmount(tjs) > tjsConfig[maxKey]) {
      setCustomAmountError(
        t(`payment.${currencyKey}.errorInvalidAmount`, {
          min: formatAmount(tjsConfig[minKey]),
          max: formatAmount(tjsConfig[maxKey])
        })
      )
      return false
    }

    return true
  }

  const handleCustomPayment = async () => {
    if (!validateCustomAmount() || isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()
    const startingBalance = useAppStore.getState().balance

    setIsProcessing(true)

    try {
      const tjs = roundAmount(Number(tjsInput))
      console.log('💰 Создание платежа (custom):', { tjs })

      // Для произвольной суммы отправляем tjs (по доке TJS)
      const payment = await (isByn ? api.createPaymentByByn(tjs) : isUah ? api.createPaymentByUah(tjs) : isKzt2 ? api.createPaymentByKzt(tjs) : isKgs ? api.createPaymentByKgs(tjs) : api.createPaymentByTjs(tjs))
      openPaymentLink(payment.link, paymentWindow, startingBalance)
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа:', error)

      if (error.message === 'INVALID_AMOUNT') {
        setCustomAmountError(t(`payment.${currencyKey}.errorInvalidAmount`, {
          min: formatAmount(tjsConfig[minKey]),
          max: formatAmount(tjsConfig[maxKey])
        }))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else if (error.message === 'LIMIT_ORDER') {
        toast.error(t(`payment.${isKzt2 ? 'kzt2' : currencyKey}.errorLimitOrder`))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const renderPackages = () => {
    if (isLoadingPackages) {
      return (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="bg-surface rounded-lg border border-white border-opacity-10 p-4 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg"></div>
                  <div>
                    <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-16"></div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                  <div className="h-6 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (packagesError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{packagesError}</p>
          <button
            onClick={loadPackages}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('common.retry')}
          </button>
        </div>
      )
    }

    if (packages.length === 0) {
      return <p className="text-text-secondary text-center py-8">{t('payment.sbp.noPackages')}</p>
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative bg-surface rounded-lg border border-white border-opacity-10 p-4 cursor-pointer transition-all hover:border-accent-primary/50 hover:scale-[1.02] active:scale-[0.98] ${
              pkg.popular ? 'ring-2 ring-accent-primary ring-opacity-50' : ''
            } ${
              isProcessing && processingPackageId === pkg.id
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={() => !isProcessing && handlePackagePayment(pkg)}
          >
            {pkg.popular && (
              <div className="absolute -top-2 -right-2">
                <span className="inline-block px-3 py-1 bg-accent-primary text-dark-primary text-xs font-bold rounded-full shadow-lg">
                  {t('payment.popular')}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gem size={28} className="text-dark-primary" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{formatCrystals(pkg.crystals)}</p>
                  <p className="text-text-secondary text-sm">{t('payment.crypto.crystals')}</p>
                  {/* Бонусные кристаллы от промокода */}
                  {pkg.promo_bonus_crystals > 0 && (
                    <p className="text-accent-primary text-xs font-semibold mt-0.5">
                      +{formatCrystals(pkg.promo_bonus_crystals)} {t('promo.bonus')}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                {isProcessing && processingPackageId === pkg.id ? (
                  <div className="flex items-center gap-2 text-accent-primary">
                    <Loader size={20} className="animate-spin" />
                  </div>
                ) : (
                  <div className="text-accent-primary font-bold text-lg">
                    {/* TJS — числа маленькие, без форматирования пробелами */}
                    <span><bdi>{formatAmount(pkg[method])} {currency}</bdi></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderCustomAmount = () => {
    if (isLoadingConfig) {
      return (
        <div className="text-center py-8">
          <Loader size={48} className="text-accent-primary mx-auto mb-4 animate-spin" />
          <p className="text-text-secondary">{t('payment.sbp.loadingConfig')}</p>
        </div>
      )
    }

    if (configError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{configError}</p>
          <button
            onClick={loadTjsConfig}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('common.retry')}
          </button>
        </div>
      )
    }

    const hasError = customAmountError !== ''
    const tjs = parseFloat(tjsInput)
    const isValidAmount =
      tjsInput &&
      !hasError &&
      Number.isFinite(tjs) &&
      roundAmount(tjs) >= tjsConfig[minKey] &&
      roundAmount(tjs) <= tjsConfig[maxKey]

    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-text-secondary text-sm">
            {t(`payment.${currencyKey}.limits`, {
              min: formatAmount(tjsConfig[minKey]),
              max: formatAmount(tjsConfig[maxKey])
            })}
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{t(`payment.${currencyKey}.${isKzt2 ? 'amountKztLabel' : `amount${currency}Label`}`)}</label>
          <input
            type="text"
            inputMode={isByn ? 'decimal' : 'numeric'}
            dir="ltr"
            value={tjsInput}
            onChange={handleTjsChange}
            placeholder={t('payment.sbp.enterAmount')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">{t('payment.crypto.crystals')}</label>
          <input
            type="text"
            inputMode="numeric"
            dir="ltr"
            value={crystalsInput}
            onChange={handleCrystalsChange}
            placeholder={t('payment.sbp.enterCrystals')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        {/* Промо-бонус для произвольной суммы */}
        {tjsConfig?.promo?.percent && crystalsInput && (
          <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-lg p-3 text-center">
            <span className="text-accent-primary font-semibold text-sm">
              +{formatCrystals(Math.ceil(parseInt(crystalsInput) * tjsConfig.promo.percent / 100))} {t('promo.bonus')}
            </span>
          </div>
        )}

        {hasError && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">{customAmountError}</p>
          </div>
        )}

        <button
          onClick={handleCustomPayment}
          disabled={!isValidAmount || isProcessing}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
            isValidAmount && !isProcessing
              ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader size={20} className="animate-spin" />
              {t(`payment.${currencyKey}.creating`)}
            </span>
          ) : (
            <bdi>{t('payment.sbp.pay')} {formatAmount(roundAmount(Number(tjsInput) || 0))} {currency}</bdi>
          )}
        </button>
      </div>
    )
  }

  if (pendingPayment) {
    return (
      <div className="max-w-md mx-auto bg-surface rounded-xl border border-white/10 p-6 text-center space-y-4">
        {paymentConfirmed ? <Gem className="mx-auto text-accent-primary" size={36} /> : <Loader className="mx-auto text-accent-primary animate-spin" size={36} />}
        <p className="text-white font-semibold">{t(paymentConfirmed ? `payment.${statusKey}.confirmed` : `payment.${statusKey}.waiting`)}</p>
        {!paymentConfirmed && <p className="text-text-secondary text-sm">{t(`payment.${statusKey}.exactAmount`)}</p>}
        {!paymentConfirmed && (
          <button onClick={() => isByn ? api.openBynPayment(pendingPayment.link) : isUah ? api.openUahPayment(pendingPayment.link) : isKzt2 ? api.openKzt2Payment(pendingPayment.link) : api.openKgsPayment(pendingPayment.link)} className="w-full py-3 rounded-lg bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold">
            {t(`payment.${statusKey}.openLink`)}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('packages')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'packages'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Package size={18} className="inline mr-2" />
            {t('payment.crypto.tabPackages')}
          </button>

          <button
            onClick={() => setActiveTab('custom')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${
                activeTab === 'custom'
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Edit3 size={18} className="inline mr-2" />
            {t('payment.crypto.tabCustom')}
          </button>
        </div>
      </div>

      {activeTab === 'packages' ? renderPackages() : renderCustomAmount()}
      {(isUah || isByn || isKzt2) && <p className="mt-4 text-center text-text-secondary text-sm">{t(`payment.${statusKey}.exactAmount`)}</p>}
    </div>
  )
}

export default TjsPayment
